// client/src/utils/ViewportManager.js
import { useState, useEffect, useCallback } from 'react';

const VIEWPORT_PREF_KEY = 'makah_viewport_mode'; // 'auto' | 'desktop' | 'mobile'
const DESKTOP_WIDTH = 1280;

/**
 * Detects whether the user's phone browser is requesting "Desktop site"
 * or if the user explicitly toggled Desktop View in our site footer.
 * Dynamically updates <meta name="viewport"> and root classes so:
 * - On PC browsers -> Desktop view
 * - On Phone browsers -> Mobile view automatically
 * - On Phone browsers with "Desktop site" checked (or manual Desktop toggle) -> Full Desktop view (1280px)
 */
export const getViewportPreference = () => {
  try {
    return localStorage.getItem(VIEWPORT_PREF_KEY) || 'auto';
  } catch {
    return 'auto';
  }
};

export const setViewportPreference = (mode) => {
  try {
    localStorage.setItem(VIEWPORT_PREF_KEY, mode);
    window.dispatchEvent(new CustomEvent('makah:viewport-mode-changed', { detail: mode }));
  } catch (err) {
    console.error('Could not save viewport preference:', err);
  }
};

const applyViewportMeta = (forceDesktop) => {
  let meta = document.querySelector('meta[name="viewport"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'viewport';
    document.head.appendChild(meta);
  }

  if (forceDesktop) {
    meta.setAttribute(
      'content',
      `width=${DESKTOP_WIDTH}, initial-scale=0.3, minimum-scale=0.2, maximum-scale=5.0, user-scalable=yes`
    );
    document.documentElement.classList.add('force-desktop-viewport');
    document.documentElement.classList.remove('mobile-viewport');
  } else {
    meta.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes'
    );
    document.documentElement.classList.remove('force-desktop-viewport');

    const isMobileScreen = window.innerWidth <= 768;
    if (isMobileScreen) {
      document.documentElement.classList.add('mobile-viewport');
    } else {
      document.documentElement.classList.remove('mobile-viewport');
    }
  }
};

const ViewportManager = () => {
  const [, setMode] = useState(getViewportPreference);

  const evaluateViewport = useCallback(() => {
    const pref = getViewportPreference();

    // Detect if a physical mobile device has its browser "Desktop site" option checked:
    // On mobile browsers, checking "Desktop site" changes userAgent to desktop (Linux x86_64 / Macintosh)
    // OR sets window.innerWidth >= 980 while screen.width / touch points indicate a mobile handset.
    const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    const smallestScreenEdge = Math.min(
      window.screen?.width || window.innerWidth,
      window.screen?.height || window.innerHeight
    );
    const isLikelyPhoneHardware = hasTouch && smallestScreenEdge <= 540;
    const uaRequestsDesktop =
      isLikelyPhoneHardware && !/Android|iPhone|iPod|Mobile/i.test(navigator.userAgent);

    if (pref === 'desktop' || (pref === 'auto' && uaRequestsDesktop)) {
      applyViewportMeta(true);
    } else {
      applyViewportMeta(false);
    }
  }, []);

  useEffect(() => {
    evaluateViewport();

    const handleModeChange = (e) => {
      setMode(e.detail || getViewportPreference());
      evaluateViewport();
    };

    const handleResize = () => {
      if (getViewportPreference() !== 'desktop') {
        const isMobileScreen = window.innerWidth <= 768;
        document.documentElement.classList.toggle('mobile-viewport', isMobileScreen);
      }
    };

    window.addEventListener('makah:viewport-mode-changed', handleModeChange);
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', evaluateViewport);

    return () => {
      window.removeEventListener('makah:viewport-mode-changed', handleModeChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', evaluateViewport);
    };
  }, [evaluateViewport]);

  return null;
};

export default ViewportManager;
